import { ACCENT, GRID, INK, INK_MUTED, SURFACE } from './palette';

/**
 * The technical argument in one picture: two stacks, side by side. On the left an HTTP
 * boundary that carries a result or an error and nothing else; on the right a contract
 * that carries capacity upward and cancellation downward. Desktop and mobile geometries
 * are separate so the text stays legible.
 */

function Box({ x, y, w, h, title, sub, accent = false }: { x: number; y: number; w: number; h: number; title: string; sub?: string; accent?: boolean }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="2" fill={SURFACE} stroke={accent ? ACCENT : GRID} strokeWidth={accent ? 1.5 : 1} />
      <text x={x + 14} y={y + 22} fontSize="12.5" fontWeight="600" fill={INK}>
        {title}
      </text>
      {sub && (
        <text x={x + 14} y={y + 39} fontSize="10.5" fill={INK_MUTED}>
          {sub}
        </text>
      )}
    </g>
  );
}

function Column({ x, accent, heads }: { x: number; accent: boolean; heads: [string, string] }) {
  const W = 250;
  return (
    <g>
      <text x={x} y={16} fontSize="10.5" fill={accent ? ACCENT : INK_MUTED} fontFamily="IBM Plex Mono, monospace" letterSpacing="0.08em">
        {heads[0]}
      </text>
      <text x={x} y={30} fontSize="10" fill={INK_MUTED}>
        {heads[1]}
      </text>
      <Box x={x} y={44} w={W} h={50} title={accent ? 'Agent framework + control layer' : 'LangChain-class framework'} sub={accent ? 'admission · budgets · cancellation-aware' : 'blocking-heritage orchestration'} accent={accent} />
      {/* the boundary */}
      {accent ? (
        <g>
          <line x1={x + 70} y1={94} x2={x + 70} y2={150} stroke={ACCENT} strokeWidth="1.5" markerEnd="url(#sc-arrow-accent)" />
          <line x1={x + W - 70} y1={150} x2={x + W - 70} y2={94} stroke={ACCENT} strokeWidth="1.5" markerEnd="url(#sc-arrow-accent)" />
          <text x={x + 78} y={118} fontSize="9.5" fill={INK_MUTED}>
            cancel
          </text>
          <text x={x + 78} y={130} fontSize="9.5" fill={INK_MUTED}>
            + budget
          </text>
          <text x={x + W - 62} y={118} fontSize="9.5" fill={INK_MUTED}>
            capacity
          </text>
          <text x={x + W - 62} y={130} fontSize="9.5" fill={INK_MUTED}>
            lease
          </text>
        </g>
      ) : (
        <g>
          <line x1={x + W / 2} y1={94} x2={x + W / 2} y2={150} stroke={INK_MUTED} strokeWidth="1.5" strokeDasharray="4 4" markerEnd="url(#sc-arrow-muted)" />
          <text x={x + W / 2 + 10} y={118} fontSize="9.5" fill={INK_MUTED}>
            HTTP request
          </text>
          <text x={x + W / 2 + 10} y={130} fontSize="9.5" fill={INK_MUTED}>
            result or error only
          </text>
        </g>
      )}
      <Box x={x} y={150} w={W} h={50} title={accent ? 'Fnk inference engine' : 'vLLM / llama.cpp'} sub={accent ? 'admission-aware scheduling · reclaim accounting' : 'sees requests, not transactions'} accent={accent} />
      <text x={x} y={226} fontSize="10.5" fill={accent ? INK : INK_MUTED}>
        {accent ? 'More completed work per GPU · bounded overload' : 'Overprovisioning · tail collapse · dead work'}
      </text>
    </g>
  );
}

export function StackContract() {
  return (
    <figure className="m-0">
      <svg viewBox="0 0 560 240" className="hidden h-auto w-full sm:block" role="img" aria-label="An assembled stack joins a framework to an inference engine over HTTP, which returns a result or an error and nothing else. The Fnk stack replaces that boundary with a contract: capacity leases flow upward from the engine, cancellation and transaction budgets flow downward from the framework.">
        <defs>
          <marker id="sc-arrow-accent" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={ACCENT} />
          </marker>
          <marker id="sc-arrow-muted" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={INK_MUTED} />
          </marker>
        </defs>
        <Column x={10} accent={false} heads={['ASSEMBLED', 'two projects joined by HTTP']} />
        <Column x={300} accent heads={['FNK AI STACK', 'one non-blocking system']} />
      </svg>
      <svg viewBox="0 0 280 500" className="h-auto w-full sm:hidden" role="img" aria-label="An assembled stack joins a framework to an inference engine over HTTP; the Fnk stack replaces that boundary with a capacity and cancellation contract.">
        <defs>
          <marker id="sc-arrow-accent-m" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={ACCENT} />
          </marker>
          <marker id="sc-arrow-muted-m" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={INK_MUTED} />
          </marker>
        </defs>
        <g>
          <text x={10} y={16} fontSize="10.5" fill={INK_MUTED} fontFamily="IBM Plex Mono, monospace" letterSpacing="0.08em">ASSEMBLED</text>
          <Box x={10} y={28} w={260} h={50} title="LangChain-class framework" sub="blocking-heritage orchestration" />
          <line x1={140} y1={78} x2={140} y2={128} stroke={INK_MUTED} strokeWidth="1.5" strokeDasharray="4 4" markerEnd="url(#sc-arrow-muted-m)" />
          <text x={150} y={106} fontSize="9.5" fill={INK_MUTED}>HTTP — result or error only</text>
          <Box x={10} y={128} w={260} h={50} title="vLLM / llama.cpp" sub="sees requests, not transactions" />
          <text x={10} y={202} fontSize="10.5" fill={INK_MUTED}>Overprovisioning · tail collapse · dead work</text>
        </g>
        <line x1={10} y1={230} x2={270} y2={230} stroke={GRID} strokeDasharray="3 5" />
        <g>
          <text x={10} y={262} fontSize="10.5" fill={ACCENT} fontFamily="IBM Plex Mono, monospace" letterSpacing="0.08em">FNK AI STACK</text>
          <Box x={10} y={274} w={260} h={50} title="Agent framework + control layer" sub="admission · budgets · cancellation-aware" accent />
          <line x1={70} y1={324} x2={70} y2={374} stroke={ACCENT} strokeWidth="1.5" markerEnd="url(#sc-arrow-accent-m)" />
          <line x1={210} y1={374} x2={210} y2={324} stroke={ACCENT} strokeWidth="1.5" markerEnd="url(#sc-arrow-accent-m)" />
          <text x={80} y={352} fontSize="9.5" fill={INK_MUTED}>cancel + budget</text>
          <text x={150} y={352} fontSize="9.5" fill={INK_MUTED}>capacity</text>
          <Box x={10} y={374} w={260} h={50} title="Fnk inference engine" sub="admission-aware scheduling · reclaim accounting" accent />
          <text x={10} y={448} fontSize="10.5" fill={INK}>More completed work per GPU · bounded overload</text>
        </g>
      </svg>
      <figcaption className="mt-3 text-[11px] leading-relaxed text-ink-3">
        The control arm of the benchmark — a reactive framework still talking to vLLM over HTTP — barely helped. The gain appears only when the
        contract crosses the boundary.
      </figcaption>
    </figure>
  );
}
